import React from 'react';
import { createPortal } from 'react-dom';
import { useFocusTrap } from '../../hooks.js';
import { BODY, MONO, TITLE, ink } from '../../primitives.js';

const h = React.createElement;

/**
 * Dialog over the scrim. Escape and backdrop click call onClose. `mono` sets the title in Space
 * Mono (for ids). Drawn into document.body through a portal, so wherever it is rendered (inside a
 * Card, whose `.gx-card { isolation: isolate }` makes a stacking context) it sits above the whole
 * page instead of under the cards that come after its parent. React events still bubble to the
 * component that rendered it.
 *
 * @param {object} p
 * @param {'gold'|'coral'|'lime'} [p.iconTone]
 * @param {React.ReactNode} p.title
 * @param {React.ReactNode} [p.sub]
 * @param {React.ReactNode} [p.icon]
 * @param {boolean} [p.mono]
 * @param {() => void} [p.onClose]
 * @param {React.ReactNode} [p.footer]
 * @param {React.ReactNode} [p.children]
 * @param {number} [p.width]
 */
export function Modal({ title, sub, icon, iconTone = 'gold', mono, onClose, footer, children, width = 520 }) {
  const box = React.useRef(null);
  const tid = React.useMemo(() => 'gx-m-' + Math.random().toString(36).slice(2, 8), []);
  if (useFocusTrap) useFocusTrap(box, true);
  React.useEffect(() => { const k = (e) => e.key === 'Escape' && onClose && onClose(); window.addEventListener('keydown', k); return () => window.removeEventListener('keydown', k); }, [onClose]);
  const tree = h('div', { onClick: onClose, className: 'gx-fade', style: { position: 'fixed', inset: 0, background: 'var(--scrim)', display: 'flex', justifyContent: 'center', alignItems: 'flex-start', paddingTop: 64, zIndex: 50 } },
    h('div', { ref: box, role: 'dialog', 'aria-modal': true, 'aria-labelledby': tid, onClick: (e) => e.stopPropagation(), className: 'gx-drop', style: { width, maxWidth: 'calc(100vw - 32px)', background: 'var(--card)', border: ink(2), borderRadius: 'var(--r-card)', boxShadow: 'var(--el-4)', padding: '20px 22px', boxSizing: 'border-box', transform: 'none', transformOrigin: '50% 0' } },
      h('div', { style: { display: 'flex', alignItems: 'flex-start', gap: 13 } },
        icon ? h('div', { style: { width: 42, height: 42, flex: 'none', display: 'grid', placeItems: 'center', border: ink(2), borderRadius: 11, background: iconTone === 'coral' ? 'var(--coral)' : iconTone === 'lime' ? 'var(--lime)' : 'var(--gold)', color: 'var(--on-accent)' } }, icon) : null,
        h('div', { style: { flex: 1, minWidth: 0 } },
          h('div', { id: tid, style: { fontFamily: mono ? MONO : TITLE, fontWeight: mono ? 700 : 800, fontSize: mono ? 17 : 'var(--type-modal-title)', letterSpacing: mono ? 0 : '-.01em', lineHeight: 1.15 } }, title),
          sub ? h('div', { style: { fontSize: 13, color: 'var(--tx2)', marginTop: 4 } }, sub) : null),
        h('button', { type: 'button', onClick: onClose, 'aria-label': 'Close', className: 'mo-lift', style: { width: 28, height: 28, border: ink(2), borderRadius: 8, background: 'var(--card)', boxShadow: 'var(--el-1)', cursor: 'pointer', fontFamily: BODY, fontWeight: 800, fontSize: 13, color: 'var(--tx)' } }, '×')),
      h('div', { style: { display: 'flex', flexDirection: 'column', gap: 16, marginTop: 18 } }, children),
      footer ? h('div', { style: { display: 'flex', gap: 10, marginTop: 20 } }, footer) : null));
  return typeof document === 'undefined' ? tree : createPortal(tree, document.body);
}
